import React, { useState } from 'react';
import { CopyIcon, XIcon } from 'assests/GmsIcons';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import ChoiceChip from 'Components/DesignSystem/ChoiceChip/ChoiceChip';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';

type TMerchantSetting = {
    config_attr: string;
    title?: string;
    name?: string;
    description?: string;
    type?: string;
    value?: any;
    lov?: string[];
};

type MerchantSettingsProps = {
    merchantSettings: TMerchantSetting[];
    environmentKey: string;
    onClose: () => void;
    onSubmit: (categories: []) => void;
}

/**
 * The Guide/ISR Settings drawer on the design system (§ 95). The 2026-08-23
 * pass already moved the string/number/boolean editors onto TextField/Toggle;
 * this finishes the surface: DS SidePanel shell (modal, Escape + backdrop
 * dismissal — the legacy fixed div at z-1000 had neither), the array editor
 * onto DS Dropdown (retiring the last Bootstrap `form-select` here), the
 * multi-value pills onto DS ChoiceChip, the info icons onto InfoTooltip, and
 * Done onto a DS Button. Value handling and the submit payload are unchanged.
 */
const MerchantSettings = ({ merchantSettings: initialSettings, environmentKey, onClose, onSubmit }: MerchantSettingsProps) => {

    const [merchantSettings, setMerchantSettings] = useState<any>(initialSettings);

    const handleBoolChange = (next: boolean, index: number) => {
        // Previously took the change event and ignored it, flipping `!e.value`
        // instead. Toggle hands back the new value, so use it rather than assuming
        // the stored one is the opposite.
        const updatedItems = merchantSettings.map((item: any, i: number) =>
            i === index ? { ...item, value: next } : item);
        setMerchantSettings(updatedItems);
    }

    const handleChangeInput = (value: string, index: number) => {
        const updatedItems = merchantSettings.map((e: any, i: number) => i === index ? { ...e, value: `${value}` } : e)
        setMerchantSettings(updatedItems);
    }

    const copyEnvironmentKey = () => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(environmentKey);
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = environmentKey;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            // @ts-ignore - execCommand is the only fallback for iOS Safari < 13.4
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        toast.success('Environment key copied to clipboard');
    };

    const renderChips = (e: any, index: number) => {
        return <div id="merchant-settings-dialog" className={`tw-mt-2 tw-flex tw-flex-wrap tw-gap-1`}>
            {e.lov.map((lovValue: any) => {
                const found = e.value.includes(lovValue);
                return <ChoiceChip
                    key={lovValue}
                    label={lovValue}
                    selected={found}
                    data-testid={`merchant-setting-chip-${e.config_attr}-${lovValue}`}
                    onClick={() => {
                        const updatedItems = merchantSettings.map((s: any, i: number) => {
                            if (i !== index) {
                                return s;
                            }
                            if (found) {
                                return { ...s, value: s.value.filter((v: any) => v !== lovValue) };
                            }
                            return { ...s, value: [...s.value, lovValue] };
                        })
                        setMerchantSettings(updatedItems);
                    }}
                />;
            })}
        </div>
    }

    const renderValues = (e: any, index: number) => {
        switch (e.type) {
            case 'array':
                return <div className="flex-none tw-w-[125px] tw-my-2">
                    {/* Was the last Bootstrap `form-select drop-down-dm` on this
                        screen — flagged "still open" in okf/isr since 2026-08-23. */}
                    <Dropdown
                        label=""
                        aria-label={`${e.title ?? e.name ?? 'Setting'} value`}
                        size="sm"
                        options={(e.lov ?? []).map((item: string) => ({ value: item, label: item }))}
                        value={e.value ?? ''}
                        onChange={(next) => handleChangeInput(String(next), index)}
                        data-testid={`merchant-setting-select-${e.config_attr}`}
                    />
                </div>;
            case 'string':
                return <div className="flex-none tw-w-[124px] tw-ml-[10px] tw-my-2">
                    {/* `label=""` keeps the row layout (the setting's name is the
                        previous column); `ariaLabel` still names the field. */}
                    <TextField
                        label=""
                        ariaLabel={`${e.name ?? 'Setting'} value`}
                        value={e.value ?? ''}
                        onChange={(next) => handleChangeInput(next, index)}
                        placeholder={'Enter ID'}
                        data-testid={`merchant-setting-input-${e.config_attr}`}
                    />
                </div>;
            case 'number':
                return <div className="flex-none tw-w-[62px] tw-ml-[10px] tw-my-2">
                    {/* `decimalAllowed` is load-bearing: TextField blocks '.' and
                        forces step="1" without it, and this field carried
                        step="any". Omitting it would silently stop decimal entry. */}
                    <TextField
                        label=""
                        ariaLabel={`${e.name ?? 'Setting'} value`}
                        type="number"
                        decimalAllowed
                        value={e.value ?? ''}
                        onChange={(next) => handleChangeInput(next, index)}
                        data-testid={`merchant-setting-input-${e.config_attr}`}
                    />
                </div>;
            case 'boolean':
                return <div className="flex-none tw-w-[50px] tw-ml-[10px] tw-my-2">
                    <Toggle
                        label=""
                        aria-label={`${e.title ?? e.name ?? 'Setting'}`}
                        checked={!!e.value}
                        onChange={(next) => handleBoolChange(next, index)}
                        data-testid={`merchant-setting-toggle-${e.config_attr}`}
                    />
                </div>;
            default:
                return '';
        }
    }

    return (
        <SidePanel
            isOpen
            onClose={onClose}
            title={
                /* Two header lines (user-called 2026-09-02): the Environment Key row
                   lives UNDER "Settings", inside the title band, so the panel opens
                   as one header block closed by the band's own § 80 rule instead of
                   a floating row between two dividers. The copy button keeps its
                   testid and handler — only the placement moved. */
                <span className="tw-flex tw-flex-col tw-gap-1">
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-font-semibold">
                        Settings
                        <InfoTooltip
                            message="Settings apply to all the models in this channel-environment"
                            label="About Settings"
                            usePortal
                        />
                    </span>
                    <span className="tw-flex tw-items-center tw-gap-2">
                        <Typography variant="body-primary" as="span" className="tw-text-ink-secondary tw-font-normal">Environment Key</Typography>
                        <Button
                            variant="subtle"
                            size="sm"
                            data-testid="pages-merchantsettings-button-6"
                            onClick={copyEnvironmentKey}
                            aria-label="Copy environment key"
                            title="Copy environment key"
                        >
                            <CopyIcon />
                        </Button>
                    </span>
                </span>
            }
            ariaLabel="Settings"
            tabs={[]}
            width="md"
            data-testid="merchant-settings-panel"
            headerActions={
                /* `self-start` pins the X to the FIRST header line: the header row is
                   items-center, and with the two-line title the X would float beside
                   the Environment Key line instead of beside "Settings". */
                <Button
                    variant="subtle"
                    size="sm"
                    className="tw-self-start"
                    aria-label="Close"
                    data-testid="pages-merchantsettings-button-5"
                    onClick={() => onClose()}
                >
                    <XIcon />
                </Button>
            }
        >
            {merchantSettings.map((e: any, index: number) => {

                /* The first row draws no top border — with the Environment Key in the
                   header, that border was a second rule 12px under the band's own,
                   the "border below the Environment Key" the user asked to remove. */
                return <div key={e.config_attr} className={`tw-items-center ${Array.isArray(e.value) ? 'tw-py-2' : ''} ${((index + 1) === merchantSettings.length) ? 'tw-border-b' : ''} ${index === 0 ? '' : 'tw-border-t'} tw-border-edge`}>
                    <div className="tw-flex">
                        <div className="tw-flex-auto tw-flex tw-items-center tw-gap-1.5">
                            <Typography variant="body-primary" as="span">{e.title}</Typography>
                            <InfoTooltip message={e.description} label={`About ${e.title ?? 'setting'}`} usePortal />
                        </div>
                        {!Array.isArray(e.value) ? renderValues(e, index) : null}
                    </div>
                    {Array.isArray(e.value) ? renderChips(e, index) : null}
                </div>;
            })}

            <Button
                variant="primary"
                className="tw-mt-4 tw-mb-6"
                data-testid="merchant-settings-done-btn"
                onClick={() => {
                    onSubmit(merchantSettings);
                }}
            >
                Done
            </Button>
        </SidePanel>
    );
}

export default MerchantSettings;
